import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { Row, Col, CardBody, Card } from "reactstrap";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../Loaders/DnaLoader";
import DataTable from 'react-data-table-component';
import axios from 'axios';
import { FaEye, FaTrash, FaEdit } from 'react-icons/fa';
import Swal from "sweetalert2";
import { Tooltip } from "@mui/material";
import PermissionAccess from '../../PermissionAccess/Access.js';
import Alerts from "../Alerts/alerts";
import AuthService from "../../pages/Authentication/auth.service";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getMerchantLogoUrl } from "../../utils/fileUtils";

const API_BASE_URL = process.env.REACT_APP_APIURL;
const FILE_BASE_URL = process.env.REACT_APP_FILE_URL || "http://localhost:3012";

const MerchantsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMerchantType, setFilterMerchantType] = useState('');
  const [filterPartner, setFilterPartner] = useState('');
  const [pageLimit, setPageLimit] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [selectedMerchantName, setSelectedMerchantName] = useState('');
  const { authData, accessPages, super_admin, current_module } = PermissionAccess();
  const canAdd = super_admin || current_module?.AddData === 1;
  const canView = super_admin || current_module?.ViewDetails === 1;
  const canDelete = super_admin || current_module?.DeleteData === 1;


  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem('language'));
    fetchMerchants(1, pageLimit);
  }, [pageLimit]);

  function fetchMerchants(page_index, page_limit, text, status, merchantType, partnerFilter) {
    setLoading(true);
    var offset = (page_index - 1) * page_limit;
    if (offset < 1) {
      offset = 0;
    }

    // Convert status string for backend
    let statusParam = '';
    if (status === 'Active') {
      statusParam = 'Active';
    } else if (status === 'InActive') {
      statusParam = 'InActive';
    }else if (status === 'Under Approval') {
      statusParam = 'Under Approval';
    } else if (status === 'Deleted') {
      statusParam = 'Deleted';
    }

    console.log('Fetching merchants with params:', { page_index, page_limit, text, status, statusParam, merchantType, partnerFilter });

    const requestData = {
      functionName: 'GetMerchants',
      postData: {
        offset: offset,
        limit: page_limit,
        sort: { createdAt: -1 },
        text: text || '',
        status: statusParam,
        merchantType: merchantType || '',
        noActiveMatch: true
      }
    };

    if (partnerFilter === 'partner') {
      requestData.postData.isPartnerPharmacy = true;
    } else if (partnerFilter === 'standard') {
      requestData.postData.isPartnerPharmacy = false;
    }

    const headers = {
      'Content-Type': 'application/json',
      'token': localStorage.getItem('session-id')
    };

    axios.post(`${API_BASE_URL}/GetMerchants`, requestData, { headers }).then(
      (response) => {
        setLoading(false);
        console.log("Get merchants response:-", response);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          console.log(response.data);
          // Debug: Log merchant logos
          if (response.data.data && response.data.data.length > 0) {
            console.log("MerchantsList - Sample merchant with logo:", {
              merchantName: response.data.data[0].merchantName,
              logo: response.data.data[0].logo,
              hasLogo: !!response.data.data[0].logo,
              hasFilename: !!(response.data.data[0].logo && response.data.data[0].logo.filename)
            });
          }
          setMerchants(response.data.data || []);
          setTotalRows(response.data.count?.count || 0);
        }
      },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        if (errContent) {
          // Check if it's an object with a message property
          if (typeof errContent === 'object' && errContent.message) {
            Alerts.swalErrorAlert(errContent.message);
          } else if (typeof errContent === 'string') {
            Alerts.swalErrorAlert(errContent);
          } else {
            Alerts.swalErrorAlert(t("Server Error, Please try again later."));
          }
        } else {
          Alerts.swalErrorAlert(t("Server Error, Please try again later."));
        }
      }
    );
  }

  function confirmDelete(_id) {
    let msg = t("Are you sure you want to delete this merchant?")
    Alerts.swalConfirmDeleteAlert(msg).then(res => {
      console.log("Resp:-", res)
      if (res === true) {
        sendDelete(_id)
      }
    })
  };

  function sendDelete(id) {
    setLoading(true);
    const requestData = {
      functionName: 'DeleteMerchant',
      postData: {
        merchant_id: id
      }
    };

    const headers = {
      'Content-Type': 'application/json',
      'token': localStorage.getItem('session-id')
    };

    axios.post(`${API_BASE_URL}/DeleteMerchant`, requestData, { headers }).then((response) => {
      console.log("Delete Merchant response:-", response)
      setLoading(false);
      fetchMerchants(currentPage, pageLimit, filterText, filterStatus, filterMerchantType, filterPartner);
      if (response.data.status) {
        Alerts.swalSuccessAlert(t("Merchant deleted successfully."))
        console.log(response.data.message)
      }
      else {
        setLoading(false);
        if (response.data.message !== '') {
          if (response.data.message === "Unauthorized") {
            AuthService.logout();
            navigate("/login");
            window.location.reload();
          }
          else {
            Alerts.swalErrorAlert(response.data.message);
          }
        }
        else {
          Alerts.swalErrorAlert(t("Error: Can't delete the Merchant."))
        }
      }
    },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) || error.message || error.toString();
        if (typeof errContent === 'object' && errContent.message) {
          Alerts.swalErrorAlert(errContent.message);
        } else if (typeof errContent === 'string') {
          Alerts.swalErrorAlert(errContent);
        } else {
          Alerts.swalErrorAlert(t("Delete operation failed"));
        }
      })
  };

  function confirmRestore(_id) {
    const msg = t("Are you sure you want to restore this merchant?");
    Alerts.swalConfirmGeneralAlert(msg).then((res) => {
      if (res === true) {
        sendRestore(_id);
      }
    });
  }

  function sendRestore(id) {
    setLoading(true);
    const requestData = {
      functionName: "RestoreMerchant",
      postData: {
        merchant_id: id,
      },
    };
    const headers = {
      "Content-Type": "application/json",
      token: localStorage.getItem("session-id"),
    };

    axios.post(`${API_BASE_URL}/RestoreMerchant`, requestData, { headers }).then(
      (response) => {
        setLoading(false);
        fetchMerchants(currentPage, pageLimit, filterText, filterStatus, filterMerchantType, filterPartner);
        if (response.data.status) {
          Alerts.swalSuccessAlert(
            response.data.message || t("Merchant restored successfully.")
          );
        } else if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          Alerts.swalErrorAlert(
            response.data.message || t("Error: Can't restore the Merchant.")
          );
        }
      },
      () => {
        setLoading(false);
        Alerts.swalErrorAlert(t("Restore operation failed"));
      }
    );
  }


  const handleSearch = (searchText, status, merchantType, partnerFilter) => {
    setFilterText(searchText);
    setFilterStatus(status);
    setFilterMerchantType(merchantType);
    setFilterPartner(partnerFilter);
    setCurrentPage(1);
    fetchMerchants(1, pageLimit, searchText, status, merchantType, partnerFilter);
  };

  const handleClear = () => {
    setFilterText('');
    setFilterStatus('');
    setFilterMerchantType('');
    setFilterPartner('');
    setCurrentPage(1);
    fetchMerchants(1, pageLimit);
  };

  function handlePageChange(page_index) {
    console.log("Page index:-", page_index)
    setCurrentPage(page_index)
    fetchMerchants(page_index, pageLimit, filterText, filterStatus, filterMerchantType, filterPartner);
  }

  async function handlePerRowsChange(newPageLimit, page_index) {
    setPageLimit(newPageLimit);
  }

  const handleViewMerchant = (id) => {
    navigate(`/merchants/view/${id}`);
  };

  const handleLogoClick = (logo, merchantName) => {
    setSelectedLogo(logo);
    setSelectedMerchantName(merchantName);
    setLogoDialogOpen(true);
  };

  const handleCloseLogoDialog = () => {
    setLogoDialogOpen(false);
    setSelectedLogo(null);
    setSelectedMerchantName('');
  };

  const mycolumns = [
    {
      name: t('Merchant Name'),
      width: "24%",
      selector: (row) => {
        return (
          <div className="d-flex align-items-center">
            <div className="me-2">
              {getMerchantLogoUrl(row.logo) ? (
                <Tooltip placement="top" title={`${t("Click to view")} ${row.merchantName} ${t("logo")}`} arrow>
                  <img
                    src={getMerchantLogoUrl(row.logo)}
                    alt={row.merchantName}
                    className="rounded-circle"
                    style={{
                      width: '32px',
                      height: '32px',
                      objectFit: 'cover',
                      border: '2px solid #e0e0e0',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease-in-out'
                    }}
                    onClick={() => handleLogoClick(row.logo, row.merchantName)}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                    }}
                    onError={(e) => {
                      console.error('Logo image failed to load for merchant:', row.merchantName, 'URL:', getMerchantLogoUrl(row.logo));
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                    }}
                  />
                </Tooltip>
              ) : (
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-white"
                  style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: '#6c757d',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  ?
                </div>
              )}
            </div>
            <div className="d-flex align-items-center gap-1">
              {row.isPartnerPharmacy && (
                <Tooltip
                  placement="top"
                  title={
                    row.partnerVisibleOnPatientApp
                      ? t("Partner Pharmacy - Visible on Patient App")
                      : t("Partner Pharmacy - Hidden on Patient App")
                  }
                  arrow
                >
                  <i
                    className="pe-7s-handshake"
                    style={{ color: '#00BFA6', fontSize: '1.1rem' }}
                  />
                </Tooltip>
              )}
              <span>{row.merchantName}</span>
            </div>
          </div>
        );
      },
      sortable: true,
    },
    {
      name: t('Contact Person'),
      selector: "contactPerson",
      width: "14%",
      sortable: true,
    },
    {
      name: t('Type'),
      selector: "merchantType",
      width: "10%",
      sortable: true,
    },
    {
      name: t('Email'),
      selector: (row) =>
        row.status === "Deleted" && row.deleted_loginEmail
          ? row.deleted_loginEmail
          : row.loginEmail,
      width: "26%",
      sortable: true,
    },
    {
      name: t('Status'),
      width: '18%',
      selector: (row) => {
        const btnStyle = {
          padding: '0.15rem 0.5rem',
          fontSize: '0.75rem',
          lineHeight: '1',
          borderRadius: '0.2rem',
          minWidth: 'auto',
        };
        return (
          <div className="d-flex flex-wrap align-items-center gap-1">
            {row.status === 'Active' ? (
              <button
                type="button"
                style={{
                  ...btnStyle,
                  borderColor: '#3ac47d',
                  backgroundColor: '#1BD35A'
                }}
                className="btn btn-success btn-hover-shine"
              >
                Active
              </button>
            ) : row.status === 'Inactive' ? (
              <button
                type="button"
                style={{
                  ...btnStyle,
                  borderColor: '#FF0000',
                  backgroundColor: 'red'
                }}
                className="btn btn-danger btn-hover-shine"
              >
                InActive
              </button>
            ) : row.status === 'Deleted' ? (
              <button
                type="button"
                style={{
                  ...btnStyle,
                  borderColor: '#6c757d',
                  backgroundColor: '#6c757d',
                  color: '#fff'
                }}
                className="btn btn-secondary btn-hover-shine"
              >
                Deleted
              </button>
            ) : (
              <button
                type="button"
                style={{
                  ...btnStyle,
                  borderColor: '#FFA500',
                  backgroundColor: 'orange'
                }}
                className="btn btn-warning btn-hover-shine"
              >
                {row.status}
              </button>
            )}
            {row.isPartnerPharmacy && (
              <span
                className="badge"
                style={{
                  backgroundColor: row.partnerVisibleOnPatientApp ? '#00BFA6' : '#6c757d',
                  fontSize: '0.7rem',
                }}
              >
                {row.partnerVisibleOnPatientApp ? t('Partner') : t('Partner (Hidden)')}
              </span>
            )}
          </div>
        );
      },
      sortable: true,
    },
    {
      name: t('Actions'),
      width: "7%",
      selector: (row) => {
        return (
          <div>
            {canView && (
              <Tooltip placement="top" title={`${t("View")} ${t("Merchant")}`} arrow onClick={() => handleViewMerchant(row._id)} style={{ cursor: "pointer" }}>
                <i className="pe-7s-look btn-icon-wrapper text-info fs-5 p-1 ViewDetails"> </i>
              </Tooltip>
            )}
            {row.status === "Deleted" ? (
              canDelete && (
                <Tooltip
                  placement="top"
                  title={t("Restore")}
                  arrow
                  onClick={() => confirmRestore(row._id)}
                  style={{ cursor: "pointer" }}
                >
                  <i className="pe-7s-back text-success btn-icon-wrapper fs-5"> </i>
                </Tooltip>
              )
            ) : (
              canDelete && (
                <Tooltip placement="top" title={`${t("Delete")} ${t("Merchant")}`} arrow onClick={() => confirmDelete(row._id)} style={{ cursor: "pointer" }}>
                  <i className="pe-7s-trash text-danger btn-icon-wrapper fs-5 DeleteData"> </i>
                </Tooltip>
              )
            )}
          </div>
        );
      }
    },
  ];

  const filteredItems = merchants;

  const conditionalRowStyles = [
    {
      when: (row) => row.isPartnerPharmacy,
      style: {
        backgroundColor: '#e6faf7',
        borderLeft: '4px solid #00BFA6',
      },
    },
  ];

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <div className="w-100 d-flex flex-wrap align-items-end gap-2 justify-content-between">
        <div className="d-flex flex-wrap align-items-end gap-2">
          <div style={{ minWidth: '160px' }}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={t("Search Text")}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(filterText, filterStatus, filterMerchantType, filterPartner);
                }
              }}
            />
          </div>
          <div style={{ minWidth: '140px' }}>
            <select
              className="form-select form-select-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">{t("Select Status")}</option>
              <option value="Active">{t("Active")}</option>
              <option value="InActive">{t("InActive")}</option>
              <option value="Under Approval">{t("Under Approval")}</option>
              <option value="Deleted">{t("Deleted")}</option>
            </select>
          </div>
          <div style={{ minWidth: '130px' }}>
            <select
              className="form-select form-select-sm"
              value={filterMerchantType}
              onChange={(e) => setFilterMerchantType(e.target.value)}
            >
              <option value="">{t("Select Type")}</option>
              <option value="Pharmacy">{t("Pharmacy")}</option>
              <option value="IoT & Lifestyle">{t("IoT & Lifestyle")}</option>
            </select>
          </div>
          <div style={{ minWidth: '140px' }}>
            <select
              className="form-select form-select-sm"
              value={filterPartner}
              onChange={(e) => setFilterPartner(e.target.value)}
            >
              <option value="">{t("Partner Status")}</option>
              <option value="partner">{t("Partner Only")}</option>
              <option value="standard">{t("Standard Only")}</option>
            </select>
          </div>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-custom-color btn-hover-shine btn btn-sm"
            onClick={() => handleSearch(filterText, filterStatus, filterMerchantType, filterPartner)}
          >
            {t("Search")}
          </button>
          <button
            type="button"
            className="btn btn-custom-color btn-hover-shine btn btn-sm"
            onClick={handleClear}
          >
            {t("Reset")}
          </button>
        </div>
      </div>
    );
  }, [filterText, filterStatus, filterMerchantType, filterPartner, t]);

  return (
    <Fragment>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <div className="bg-light-gray bg-animation">
            <div className="d-flex justify-content-center align-items-center">
              <Col md="12" className="mx-auto app-login-box">
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <div className="page-title-heading fw-bold">
                      <div className="page-title-icon">
                        <i className="pe-7s-users icon-gradient bg-tempting-azure" />
                      </div>
                      <div>
                        {`${t("Merchants")} ${t("Management")}`}
                        <div className="page-title-subheading">
                          {`${t("Details of")} ${t("Merchants")}`}
                        </div>
                      </div>
                    </div>
                    <div className="page-title-actions">
                      {canAdd && (
                        <button type="button" className="btn btn-custom-color btn-hover-shine btn AddData" onClick={() => navigate("/merchants/create")}>
                          {t('Add New')} {t('Merchant')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <Row>
                  <LoadingOverlay
                    tag="div"
                    active={loading}
                    styles={{ overlay: (base) => ({ ...base }) }}
                    spinner={<DNALoader />}
                  >
                    <Col md="12">
                      <Card className="main-card mb-3">
                        <CardBody>
                          <DataTable
                            data={filteredItems}
                            columns={mycolumns}
                            pagination
                            fixedHeader
                            fixedHeaderScrollHeight="400px"
                            subHeader
                            subHeaderComponent={subHeaderComponentMemo}
                            conditionalRowStyles={conditionalRowStyles}
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            paginationResetDefaultPage={resetPaginationToggle}
                          />
                        </CardBody>
                      </Card>
                    </Col>
                  </LoadingOverlay>
                </Row>
              </Col>
            </div>
          </div>
        </div>
      </div>

      {/* Logo Dialog */}
      <Dialog
        open={logoDialogOpen}
        onClose={handleCloseLogoDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '0'
          }
        }}
      >
        <DialogTitle style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px 10px 24px',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <span style={{ fontSize: '18px', fontWeight: '600' }}>
            {selectedMerchantName} - {t('Logo')}
          </span>
          <IconButton
            onClick={handleCloseLogoDialog}
            style={{
              color: '#666',
              padding: '4px'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{
          padding: '24px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px'
        }}>
          {selectedLogo && (
            <img
              src={getMerchantLogoUrl(selectedLogo)}
              alt={`${selectedMerchantName} - Large View`}
              style={{
                maxWidth: '100%',
                maxHeight: '400px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          )}
          <div
            style={{
              display: 'none',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666',
              fontSize: '16px'
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>?</div>
            <span>{t('Logo not available')}</span>
          </div>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

export default MerchantsPage;
